import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { UseCompanyId, UseUserId, UseIsAdmin } from "../../hooks/auth";
import FeedItem from "./FeedItem";
import FeedCard from "./FeedCard";
import FeedReviewCard from "./FeedReviewCard";
import { GET_CLAIMED_REVIEWS } from "../../api/claims";
import YardSignPost, { useYardSignGroups } from "./YardSignPost";
import LazyFeedItem from "./LazyFeedItem";
import { useDispatch, useSelector } from "react-redux";
import { getJob, getJobs, resetJobs } from "../../redux/jobs/actions";
import Stack from "@mui/material/Stack";
import { orderBy } from "lodash";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";
import FeedTabPanel from "./FeedTabPanel";
import useMediaQuery from "@mui/material/useMediaQuery";
import TuneIcon from "@mui/icons-material/Tune";
import { UseGroupKpis, UseCompanyKpis } from "../../hooks/kpi";
import { GET_USER_KPIS } from "../../api/kpi";
import socket from "../../configs/socket";
import socketConstants from "../../constants/socket";

// Imported Styles
import "./feedStyles.css";
import InfiniteScroll from "react-infinite-scroll-component";

// Imported Images and Icons
import { Typography, Grid, Tabs, Tab, useTheme, Modal, Backdrop, Fade, Slide, Dialog } from "@mui/material";
import Icon from "../../components/Icons/Icon";
import PageHeader from "../../components/PageHeader";
import { fontWeight, minHeight, minWidth } from "@mui/system";
import { KudosRail, useKudosController, ClapConfettiCanvas } from "./Kudos";
import { glassModalPaperStyle, GlassModalCloseButton, glassModalDividerStyle } from "../../components/GlassModal";
import { SettingRow, SettingSection } from "../../components/SettingsList";
import { UseCompanyColors } from "../../hooks/company";
import { DEFAULT_BRAND, brandRgba, brandDarken } from "../../helpers/brandColor";

const { SOCKET_EVENT } = socketConstants;

// Native "push in from the right" navigation transition for the mobile
// full-screen Settings page — a stable module-scope component (not defined
// inside FeedPage) so MUI's Dialog doesn't see a new type every render.
const SlideLeftTransition = forwardRef((props, ref) => <Slide direction="left" ref={ref} {...props} />);
SlideLeftTransition.displayName = "SlideLeftTransition";

// A plain chevron rather than a Material-style arrow-with-shaft — the
// native iOS/mobile "back" convention, more familiar as a mobile back icon.
const ArrowBackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 5.5 8 12l7 6.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsGearIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6h10M17.8 6h2.2M4 18h10M17.8 18h2.2M4 12h16" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="14.5" cy="6" r="2.3" fill="#fff" />
    <circle cx="9.5" cy="18" r="2.3" fill="#fff" />
  </svg>
);

// One small line icon per setting row — gives each row a distinct visual
// anchor instead of every row reading as identical text + toggle.
const ROW_ICONS = {
  customerName: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3.5" y="5" width="17" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="9.3" cy="11" r="1.8" stroke="currentColor" strokeWidth="1.7" />
      <path d="M6.8 15.5c.6-1.4 1.7-2 2.5-2s1.9.6 2.5 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M14 10h3.2M14 13h3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  address: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="12" cy="9.5" r="2.3" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  service: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 7.5a3.7 3.7 0 0 1-4.9 3.5L8 18.1a1.8 1.8 0 0 1-2.6-2.6L12.5 8a3.7 3.7 0 0 1 3.6-4.9c.6 0 1.1.1 1.6.4l-2.6 2.6.6 2 2 .6 2.6-2.6c.2.5.3 1 .3 1.4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  ),
  jobTotal: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="8.3" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 7.3v9.4M14.5 9.6c0-1-1.1-1.8-2.5-1.8s-2.5.7-2.5 1.7c0 2.4 5 1.1 5 3.5 0 1-1 1.8-2.5 1.8s-2.6-.7-2.7-1.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  jobTime: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="8.3" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  locationPin: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 4.3 4 6.3v14l5-2 6 2 5-2v-14l-5 2-6-2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M9 4.3v14M15 6.3v14" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  photo: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="3.1" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  review: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3.7 14.5 9l5.9.9-4.3 4.1 1 5.8L12 17l-5.1 2.8 1-5.8-4.3-4.1L9.5 9 12 3.7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  mxplan: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3.3 19 6v5c0 5-3 8.3-7 9.7-4-1.4-7-4.7-7-9.7V6l7-2.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  upsell: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.5 17 9 11.5l3.5 3.5L20.5 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14.5 7H20.5V13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  yardsign: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3v18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M12 6h6.5L17 8.3 18.5 10.6H12" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M9 21h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  revenuecredit: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="12" cy="7" rx="6.2" ry="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5.8 7v10c0 1.4 2.8 2.6 6.2 2.6s6.2-1.2 6.2-2.6V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M5.8 12c0 1.4 2.8 2.6 6.2 2.6s6.2-1.2 6.2-2.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
};

const SECTION_ICONS = {
  user: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="8" r="3.3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  briefcase: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="7.5" width="18" height="11.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.5 7.5V5.8A1.8 1.8 0 0 1 10.3 4h3.4a1.8 1.8 0 0 1 1.8 1.8V7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M3 13h18" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
  image: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="4.5" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="8.5" cy="9.5" r="1.4" stroke="currentColor" strokeWidth="1.8" />
      <path d="M21 15.5l-5.2-5-4.3 4-2.8-2.5L3 16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chart: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="12" width="3.6" height="8" rx="1" fill="currentColor" />
      <rect x="10.2" y="7" width="3.6" height="13" rx="1" fill="currentColor" />
      <rect x="16.4" y="3" width="3.6" height="17" rx="1" fill="currentColor" />
    </svg>
  ),
};

const FeedPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const { isLoading, isFetching, jobs, currentPage, hasMore } = useSelector(
    (state) => state.jobsReducer,
  );

  // A notification click can land here (instead of a single-item permalink,
  // which only jobs have) asking to scroll to and open a specific review or
  // yard sign post. Memoized on the underlying primitives (not just
  // `location.state`, which is a fresh object every history push, and not
  // recomputed as a bare object literal every render either) so the effect
  // below only re-runs when the actual target changes.
  const scrollToId = location.state?.scrollToId;
  const scrollToType = location.state?.scrollToType;
  const scrollOpenComments = !!location.state?.openComments;
  const scrollTarget = useMemo(
    () => (scrollToId ? { id: String(scrollToId), type: scrollToType, openComments: scrollOpenComments } : null),
    [scrollToId, scrollToType, scrollOpenComments],
  );
  const [scrollTargetFound, setScrollTargetFound] = useState(false);
  const scrollAttempts = useRef(0);
  const userId = UseUserId();
  const isAdmin = UseIsAdmin();
  // Oldest loaded job — yard signs load back to its month, not the whole history.
  const oldestJobDate = useMemo(
    () => (jobs || []).reduce((min, j) => (j?.extRefDate && (!min || j.extRefDate < min) ? j.extRefDate : min), null),
    [jobs],
  );
  const yardSignGroups = useYardSignGroups(oldestJobDate);
  const { primaryColor: companyBrandColor } = UseCompanyColors();
  const brand = companyBrandColor || DEFAULT_BRAND;
  const brandDark = brandDarken(brand, 0.18);
  const [page, setPage] = useState(1);
  const [checked, setChecked] = React.useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [settings, setSettings] = useState(() => {
    const savedSettings = window.sessionStorage.getItem('feedSettings');
    if (savedSettings) {
      return JSON.parse(savedSettings);
    }
    const defaultSettings = {
      customerName: true,
      address: true,
      jobTotal: false,
      jobTime: true,
      locationPin: true,
      photo: true,
      review: true,
      mxplan: true,
      upsell: true,
      yardsign: true,
      service: true,
      revenuecredit: true
    };
    window.sessionStorage.setItem('feedSettings', JSON.stringify(defaultSettings));
    return defaultSettings;
  });

  // Temporary settings state for modal
  const [tempSettings, setTempSettings] = useState(settings);

  const handleChange = (event) => {
    dispatch(resetJobs());
    setPage(1);
    setChecked(event.target.checked);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const [tabValue, setTabValue] = useState(0);
  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  // eslint-disable-next-line no-shadow
  const fetchJobs = (companyId, page, checked) => {
    dispatch(getJobs({ companyId, page, userId: checked ? userId : "" }));
  };

  const companyId = UseCompanyId();
  const prevCompanyIdRef = useRef(null);

  // Soft cache: wipe only when company changes (not on remount same company).
  useEffect(() => {
    if (!companyId) return undefined;
    if (
      prevCompanyIdRef.current &&
      String(prevCompanyIdRef.current) !== String(companyId)
    ) {
      dispatch(resetJobs());
      setPage(1);
    }
    prevCompanyIdRef.current = companyId;
    return undefined;
  }, [companyId, dispatch]);

  useEffect(
    () => {
      if (companyId) {
        fetchJobs(companyId, page, checked);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, dispatch, page, checked],
  );

  // Company feed room — push new/updated jobs without remount or list poll.
  useEffect(() => {
    if (!companyId) return undefined;

    socket.emit(SOCKET_EVENT.JOIN_COMPANY_FEED, companyId);

    const onFeedJobUpserted = (data) => {
      if (!data || String(data.companyId) !== String(companyId)) return;
      let ids = [];
      if (Array.isArray(data.jobIds)) {
        ids = data.jobIds;
      } else if (data.jobId) {
        ids = [data.jobId];
      }
      ids.forEach((jobId) => {
        if (!jobId) return;
        dispatch(
          getJob(
            checked
              ? { jobId, filterUserId: userId }
              : jobId,
          ),
        );
      });
    };

    socket.on(SOCKET_EVENT.FEED_JOB_UPSERTED, onFeedJobUpserted);

    return () => {
      socket.emit(SOCKET_EVENT.LEAVE_COMPANY_FEED, companyId);
      socket.off(SOCKET_EVENT.FEED_JOB_UPSERTED, onFeedJobUpserted);
    };
  }, [companyId, dispatch, checked, userId]);

  const jobsByRecentDate = orderBy(jobs, ["extRefDate"], "desc");

  // Filter jobs based on search query
  const filteredJobs = jobsByRecentDate.filter((job) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      job.metadata?.customerName?.toLowerCase().includes(searchLower) ||
      job.metadata?.description?.toLowerCase().includes(searchLower) ||
      job.metadata?.location?.toLowerCase().includes(searchLower) ||
      job.user?.firstName?.toLowerCase().includes(searchLower) ||
      job.user?.lastName?.toLowerCase().includes(searchLower) ||
      job.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  const NavTabs = [{ label: "All Feeds" }, { label: "My Jobs" }];

  const [openModal, setOpenModal] = useState(false);

  const handleCloseModal = () => {
    setOpenModal(false);
    // Reset temp settings to current settings when closing without saving
    setTempSettings(settings);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
    // Initialize temp settings with current settings
    setTempSettings(settings);
  };

  const handleSettingChange = (settingKey) => {
    setTempSettings(prev => ({
      ...prev,
      [settingKey]: !prev[settingKey]
    }));
  };

  const handleUpdateSettings = () => {
    setSettings(tempSettings);
    window.sessionStorage.setItem('feedSettings', JSON.stringify(tempSettings));
    setOpenModal(false);
  };

  // Solid paper to match the mobile full-screen page's opaque background —
  // the shared frosted-glass style (rgba white + blur) reads as washed-out
  // here since feed content shows through behind it.
  const styleModal = {
    ...glassModalPaperStyle({ mini: "90%", xs: "90%", sm: "600px" }),
    background: "#FFFFFF",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const enabledSettingsCount = Object.values(tempSettings).filter(Boolean).length;
  const totalSettingsCount = Object.keys(tempSettings).length;

  // Shared between the desktop modal and the mobile full-screen page — only
  // the surrounding chrome (header/footer/container) differs between them.
  const settingsListContent = (
    <>
      <SettingSection title="Customer Information" icon={SECTION_ICONS.user} brand={brand}>
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.customerName}
          title="Customer Name"
          subtitle="Show the customer's name on feed updates."
          checked={tempSettings.customerName}
          onChange={() => handleSettingChange('customerName')}
        />
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.address}
          title="Customer Address"
          subtitle="Show the customer's address on feed updates."
          checked={tempSettings.address}
          onChange={() => handleSettingChange('address')}
        />
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.service}
          title="Service"
          subtitle="Show the service on feed updates."
          checked={tempSettings.service}
          onChange={() => handleSettingChange('service')}
        />
      </SettingSection>

      <SettingSection title="Job Info" icon={SECTION_ICONS.briefcase} brand={brand}>
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.jobTotal}
          title="Job Total"
          subtitle="Show the total value of the job."
          checked={tempSettings.jobTotal}
          onChange={() => handleSettingChange('jobTotal')}
        />
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.jobTime}
          title="Job Time"
          subtitle="Display the time spent on the job."
          checked={tempSettings.jobTime}
          onChange={() => handleSettingChange('jobTime')}
        />
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.locationPin}
          title="Map / Location Pin"
          subtitle="Include a map snapshot of the job location."
          checked={tempSettings.locationPin}
          onChange={() => handleSettingChange('locationPin')}
        />
      </SettingSection>

      <SettingSection title="Media" icon={SECTION_ICONS.image} brand={brand}>
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.photo}
          title="Before & After Photos"
          subtitle="Add photos that highlight the job results."
          checked={tempSettings.photo}
          onChange={() => handleSettingChange('photo')}
        />
      </SettingSection>

      <SettingSection title="KPI Statistics" icon={SECTION_ICONS.chart} brand={brand}>
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.upsell}
          title="Upsell"
          subtitle="Show Upsell"
          checked={tempSettings.upsell}
          onChange={() => handleSettingChange('upsell')}
        />
        <SettingRow
          brand={brand}
          icon={ROW_ICONS.revenuecredit}
          title="Revenue Credit"
          subtitle="Show Revenue Credit"
          checked={tempSettings.revenuecredit}
          onChange={() => handleSettingChange('revenuecredit')}
        />
      </SettingSection>
    </>
  );

  const desktopSettingsModal = (
    <Modal
      open={openModal}
      onClose={handleCloseModal}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={openModal}>
        <SoftBox sx={styleModal}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
            <SoftBox display="flex" alignItems="center" gap="12px">
              <SoftBox
                sx={{
                  width: "38px",
                  height: "38px",
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  background: `linear-gradient(135deg, ${brand} 0%, ${brandDark} 100%)`,
                  boxShadow: `0px 6px 14px ${brandRgba(brand, 0.3)}`,
                }}
              >
                <SettingsGearIcon />
              </SoftBox>
              <SoftBox>
                <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "17px!important", fontWeight: "700!important", color: "#101828", lineHeight: "20px" }} component="h2">
                  Feed Settings
                </SoftTypography>
                <SoftTypography sx={{ fontSize: "12px!important", fontWeight: "600!important", color: brand }}>
                  {enabledSettingsCount} of {totalSettingsCount} shown
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <GlassModalCloseButton onClick={handleCloseModal} />
          </SoftBox>
          <hr style={glassModalDividerStyle} />

          {/* A viewport-relative calc() height is always "definite" CSS-wise
              — unlike flex:1 inside a box whose own height is content-driven
              (auto), which several engines size inconsistently once content
              is short enough not to need scrolling, letting content spill
              past its flex item and overlap the footer below it. */}
          <SoftBox sx={{ position: "relative", marginTop: "1rem" }}>
            <SoftBox
              sx={{
                maxHeight: "calc(88vh - 190px)",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                // Right padding keeps the native scrollbar clear of the
                // toggle switches instead of overlapping them.
                paddingRight: "12px",
                paddingBottom: "0.75rem",
                "& > *:not(:last-child)": {
                  paddingBottom: "1.5rem",
                  marginBottom: "1.5rem",
                  borderBottom: "1px solid rgba(15,23,42,0.09)",
                },
              }}
            >
              {settingsListContent}
            </SoftBox>
          </SoftBox>

          <SoftBox sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.25rem' }}>
            <SoftButton
              onClick={handleCloseModal}
              sx={{
                background: "#FFFFFF",
                border: "1px solid #D0D5DD",
                borderRadius: "8px",
                color: "#344054",
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                padding: "0 18px",
                height: "40px",
                boxShadow: "none",
                "&:hover": { background: "#F9FAFB", boxShadow: "none" },
              }}
            >
              Cancel
            </SoftButton>
            <SoftButton
              onClick={handleUpdateSettings}
              sx={{
                background: 'var(--gmb-primary, #1B598E)',
                border: 0,
                borderRadius: "8px",
                color: '#FFFFFF !important',
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "none",
                padding: "0 20px",
                height: "40px",
                boxShadow: "none",
                "&:hover": { background: 'var(--gmb-primary, #1B598E)', filter: 'brightness(0.96)', boxShadow: "none" },
              }}
            >
              Update Settings
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Fade>
    </Modal>
  );

  // Mobile: a dedicated full-screen page that slides in from the right, like
  // navigating to a native settings screen, instead of a centered overlay —
  // back arrow instead of an X, sticky header/footer, full-width content.
  const mobileSettingsPage = (
    <Dialog
      open={openModal}
      onClose={handleCloseModal}
      fullScreen
      TransitionComponent={SlideLeftTransition}
      PaperProps={{
        sx: {
          background: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <SoftBox
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "14px 16px",
          borderBottom: "1px solid rgba(15,23,42,0.08)",
        }}
      >
        <SoftBox
          component="button"
          type="button"
          onClick={handleCloseModal}
          aria-label="Back"
          sx={{
            border: 0,
            background: "transparent",
            padding: "6px",
            margin: "-6px",
            cursor: "pointer",
            display: "inline-flex",
            color: "#344054",
            flexShrink: 0,
          }}
        >
          <ArrowBackIcon />
        </SoftBox>
        <SoftTypography sx={{ fontFamily: "DM Sans", fontSize: "16px!important", fontWeight: "700!important", color: "#101828", lineHeight: "19px", minWidth: 0 }} component="h1">
          Feed Settings
        </SoftTypography>
      </SoftBox>

      <SoftBox
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          "& > *:not(:last-child)": {
            paddingBottom: "1.5rem",
            marginBottom: "1.5rem",
            borderBottom: "1px solid rgba(15,23,42,0.09)",
          },
        }}
      >
        {settingsListContent}
      </SoftBox>

      <SoftBox
        sx={{
          flexShrink: 0,
          display: "flex",
          gap: "10px",
          padding: "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
          borderTop: "1px solid rgba(15,23,42,0.08)",
          background: "#FFFFFF",
        }}
      >
        <SoftButton
          onClick={handleCloseModal}
          sx={{
            flex: 1,
            background: "#FFFFFF",
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
            color: "#344054",
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            height: "44px",
            boxShadow: "none",
            "&:hover": { background: "#F9FAFB", boxShadow: "none" },
          }}
        >
          Cancel
        </SoftButton>
        <SoftButton
          onClick={handleUpdateSettings}
          sx={{
            flex: 1,
            background: 'var(--gmb-primary, #1B598E)',
            border: 0,
            borderRadius: "8px",
            color: '#FFFFFF !important',
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            height: "44px",
            boxShadow: "none",
            "&:hover": { background: 'var(--gmb-primary, #1B598E)', filter: 'brightness(0.96)', boxShadow: "none" },
          }}
        >
          Update Settings
        </SoftButton>
      </SoftBox>
    </Dialog>
  );

  const [allKpis, setAllKpis] = useState([]);
  const [message, setMessage] = useState(null);
  const [claimedReviews, setClaimedReviews] = useState([]);
  const groupKpis = UseGroupKpis();
  const companyKpis = UseCompanyKpis();

  // Claimed reviews surface as their own feed cards (reviewer + review text +
  // who claimed it). This wiring shipped in 09676cd0 but was dropped in a
  // later FeedPage refactor, which is why Claims posts vanished from the feed.
  useEffect(() => {
    if (!companyId) return;
    GET_CLAIMED_REVIEWS()
      .then(({ data }) => setClaimedReviews(Array.isArray(data) ? data : []))
      .catch(() => setClaimedReviews([]));
  }, [companyId]);

  // Active "Revenue Credit" KPI id(s) across every workspace shown in the feed.
  // Previously this was a single hardcoded id ("689de73d…") that pointed at a
  // DISABLED KPI, so the revenue-credit amount on feed cards never populated.
  // Resolve it dynamically from the loaded company KPIs instead, skipping
  // disabled metrics, so each job's revenue credit is matched to whatever KPI
  // the sync actually wrote it under.
  const revenueCreditKpiIds = useMemo(() => {
    const ids = new Set();
    Object.values(companyKpis || {}).forEach((list) => {
      (list || []).forEach((k) => {
        if (k?.disabled !== true && /revenue credit/i.test(k?.title || "")) {
          ids.add(k._id);
        }
      });
    });
    return [...ids];
  }, [companyKpis]);

  useEffect(() => {
    if (!companyId || !revenueCreditKpiIds.length) return;

    Promise.all(
      revenueCreditKpiIds.map((id) =>
        GET_USER_KPIS(companyId, null, id)
          .then(({ data }) => (Array.isArray(data) ? data : []))
          .catch(() => []),
      ),
    )
      .then((results) => {
        const eKpis = results.flat();
        if (eKpis.length === 0) {
          setMessage("No recent records found");
          setAllKpis([]);
          return;
        }

        // Sort most recent first (createdAt/updatedAt, fallback to _id which is
        // time-sortable) so a job's latest revenue-credit entry wins on match.
        const sortedKpis = [...eKpis].sort((a, b) => {
          const aTime = a.updatedAt || a.createdAt || a._id;
          const bTime = b.updatedAt || b.createdAt || b._id;
          if (typeof aTime === "string" && typeof bTime === "string") {
            return bTime.localeCompare(aTime);
          }
          return 0;
        });

        const allKpisData = sortedKpis.map((ekpi) => ({
          amount: ekpi.amount,
          points: ekpi.points,
          refId: ekpi?.metadata?.refId || ekpi.refId,
          // External (HCP) job id stored on the revenue-credit entry. Used as a
          // fallback link to the job when the internal refId is missing — e.g.
          // entries synced before the server started resolving Job._id, or not
          // yet backfilled. entryId === Job.extRefId for the lead, and
          // `${Job.extRefId}-2` for a secondary tech.
          entryId: ekpi.entryId,
          kpiId: ekpi.kpiId,
          ownerId: ekpi.ownerId,
        }));

        setAllKpis(allKpisData);
      })
      .catch((err) => {
        setAllKpis([]);
        setMessage(err.message);
      });
  }, [companyId, revenueCreditKpiIds]);

  // Combine jobsByRecentDate and allKpis: attach amount and points to each job if matching refId
  const jobsWithKpis = filteredJobs.map(job => {
    // Find the revenue-credit entry for this job. Prefer the job poster's own
    // entry (ownerId === job.userId) so an assistant's reduced credit doesn't
    // surface on the lead's card; fall back to any entry for the job.
    //
    // Matching is layered so a job's credit still resolves even when the
    // internal refId link is absent:
    //   1) internal refId === job._id, poster's own entry
    //   2) internal refId === job._id (any owner)
    //   3) external id: entryId === job.extRefId, poster's own entry
    //   4) external id: entryId === job.extRefId (any owner)
    // The `-2` suffix on a secondary tech's entryId is stripped before compare.
    const jobExtId = job.extRefId ? String(job.extRefId) : null;
    const baseEntryId = (eid) => String(eid || "").replace(/-2$/, "");
    const matchedKpi =
      allKpis.find(kpi => kpi.refId === job._id && String(kpi.ownerId) === String(job.userId)) ||
      allKpis.find(kpi => kpi.refId === job._id) ||
      (jobExtId &&
        allKpis.find(
          kpi =>
            baseEntryId(kpi.entryId) === jobExtId &&
            String(kpi.ownerId) === String(job.userId),
        )) ||
      (jobExtId && allKpis.find(kpi => baseEntryId(kpi.entryId) === jobExtId));
    if (matchedKpi) {
      // Attach amount and points to the job
      return {
        ...job,
        rcAmount: matchedKpi.amount,
        rcPoints: matchedKpi.points,
        revenuecredit: true
      };
    }
    // If no kpi found, return job as is
    return job;
  });

  // Merge Yard Sign day-groups into the same chronological list as regular
  // job posts — sorted by activity time like everything else, not pinned
  // above in a separate section. Shown to everyone (admins and players
  // alike), the same way on both mobile and web.
  // Claimed-review feed items, filtered by the same search box (reviewer name,
  // review text, or the claimer's name).
  const reviewItems = useMemo(() => {
    const search = searchQuery.toLowerCase();
    return (claimedReviews || [])
      .filter((review) => {
        if (!search) return true;
        const claimerNames = (review.claimers || [])
          .map((u) => `${u.firstName || ""} ${u.lastName || ""}`)
          .join(" ")
          .toLowerCase();
        return (
          review.name?.toLowerCase().includes(search) ||
          review.content?.toLowerCase().includes(search) ||
          review.service?.toLowerCase().includes(search) ||
          claimerNames.includes(search)
        );
      })
      .map((review) => ({
        postType: "review",
        timestamp: review.claimedAt || review.extRefDate || review.updatedAt,
        review,
      }));
  }, [claimedReviews, searchQuery]);

  const feedPosts = useMemo(() => {
    const jobItems = jobsWithKpis.map((job) => ({ postType: "job", timestamp: job.extRefDate, job }));
    const yardItems = yardSignGroups.map((group) => ({ postType: "yardsign", timestamp: group.timestamp, group }));
    return [...jobItems, ...yardItems, ...reviewItems].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [jobsWithKpis, yardSignGroups, reviewItems]);

  // FeedTabPanel fully unmounts whichever tab isn't active, so a scroll
  // target sitting in "All Feeds" (reviews/yard signs never appear under "My
  // Jobs") would never be found if the user had switched tabs before. Also
  // resets the found-flag/attempt budget for this new target — without this,
  // clicking a second notification while already on /feed would find
  // scrollTargetFound still true from the first and silently do nothing.
  useEffect(() => {
    if (!scrollTarget) return;
    setTabValue(0);
    setScrollTargetFound(false);
    scrollAttempts.current = 0;
  }, [scrollTarget]);

  // Scroll to + briefly highlight the post a notification pointed at. Reviews
  // and yard signs have no single-item page, so this is how "go to where the
  // notification came from" works for them — jobs get a real /feed/:jobId
  // page instead and never set scrollTarget. If the target isn't in the
  // currently-loaded page(s) yet (an older post), keep paging in more until
  // it turns up or there's nothing left to load.
  useEffect(() => {
    if (!scrollTarget || scrollTargetFound) return;

    const el = document.getElementById("feed-scroll-target");
    if (el) {
      setScrollTargetFound(true);
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("feed-post-highlight");
      setTimeout(() => el.classList.remove("feed-post-highlight"), 2200);
      return;
    }

    if (hasMore && !isFetching && scrollAttempts.current < 25) {
      scrollAttempts.current += 1;
      setPage((prev) => Math.max(prev, currentPage) + 1);
    }
  }, [scrollTarget, scrollTargetFound, feedPosts, hasMore, isFetching, currentPage]);

  // Kudos suggestions derived from the visible feed jobs
  const kudosController = useKudosController(jobsWithKpis);

  // Every brand-colored surface reads off UseCompanyColors() via
  // `brand`/`brandDark` (computed above), so it re-tints automatically with
  // the company's Theme Colors setting — never hardcoded to a specific blue.
  const brandGradient = `linear-gradient(135deg, ${brand} 0%, ${brandDark} 100%)`;

  // The All feeds / My jobs toggle. Rendered inside the header banner on
  // every breakpoint (via PageHeader's `tabs` prop). The selected pill
  // (MuiTabs-indicator, painted full-height by the theme's tabs override)
  // is overridden here to a brand gradient instead of the theme's flat
  // primary.main, scoped to just this control.
  const tabsEl = (
    <Tabs
      orientation="horizontal"
      value={tabValue}
      onChange={(event, newValue) => setTabValue(newValue)}
      sx={{
        background: "#fff!important",
        borderRadius: "8px",
        padding: "4px",
        border: "1px solid #EAECF0",
        width: "max-content",
        minHeight: "unset",
        "& .MuiTabs-indicator": { borderRadius: "6px", boxShadow: `0 2px 6px ${brandRgba(brand, 0.35)}`, background: brandGradient },
      }}
    >
      {NavTabs.map((nav) => (
        <Tab
          key={nav.label}
          label={nav.label}
          icon={nav.icon}
          iconPosition="start"
          sx={{
            color: "#343A40",
            fontSize: "0.8rem",
            minHeight: "32px",
            py: "6px",
            px: "16px",
            zIndex: 1,
            "&.Mui-selected": { color: "#fff" },
          }}
        />
      ))}
    </Tabs>
  );

  return (
    <>
      {/* Single full-viewport <canvas> for clap confetti (GPU-accelerated). */}
      <ClapConfettiCanvas />
      <PageHeader
        headerText="Team Feed"
        subheaderText={
          isMobile
            ? "Get the real-time updates from your teammates with this private social media network"
            : "Get the real-time updates from your teammates with this private social<br/> media network built by Game My Biz for you. Give kudos, share videos,<br/> pictures."
        }
        isMobile={isMobile}
        tabs={tabsEl}
      />

      <SoftBox sx={{ marginX: { mini: "1rem", sm: "1.5rem" }, marginTop: { mini: "10px", sm: 0 }, marginBottom: { mini: "0.5rem", sm: "1.5rem" } }}>
        <Grid container>
          <Grid item mini={12} mb={isMobile ? 1 : 3}>
            <Grid container alignItems="center">
              <Grid item md={12} mini={12} px={0} mt={0}>
                <SoftBox sx={{ display: "flex", alignItems: "stretch", gap: "0.5rem", width: "100%" }}>
                  <SoftBox
                    sx={{
                      position: "relative",
                      flexGrow: 1,
                      minWidth: isMobile ? 0 : "300px",
                      display: "flex",
                      alignItems: "center",
                      height: isMobile ? "38px" : "46px",
                      background: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: "12px",
                      transition: "background-color 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
                      "&:focus-within": {
                        borderColor: brand,
                        boxShadow: `0 0 0 4px ${brandRgba(brand, 0.14)}`,
                      },
                      "&:focus-within .feed-search-icon": { color: brand },
                    }}
                  >
                    {/* Leading search icon — the standard visual cue that this
                        is a search field, not a generic text input. */}
                    <SoftBox
                      className="feed-search-icon"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        paddingLeft: isMobile ? "13px" : "17px",
                        paddingRight: "8px",
                        color: "#9AA4B2",
                        transition: "color 150ms ease",
                      }}
                    >
                      <svg width={isMobile ? "16" : "18"} height={isMobile ? "16" : "18"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21.0002 21L16.6572 16.657M16.6572 16.657C17.4001 15.9141 17.9894 15.0321 18.3914 14.0615C18.7935 13.0909 19.0004 12.0506 19.0004 11C19.0004 9.94936 18.7935 8.90905 18.3914 7.93842C17.9894 6.96779 17.4001 6.08585 16.6572 5.34296C15.9143 4.60007 15.0324 4.01078 14.0618 3.60874C13.0911 3.20669 12.0508 2.99976 11.0002 2.99976C9.9496 2.99976 8.90929 3.20669 7.93866 3.60874C6.96803 4.01078 6.08609 4.60007 5.34321 5.34296C3.84288 6.84329 3 8.87818 3 11C3 13.1217 3.84288 15.1566 5.34321 16.657C6.84354 18.1573 8.87842 19.0002 11.0002 19.0002C13.122 19.0002 15.1569 18.1573 16.6572 16.657Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </SoftBox>
                    <SoftBox
                      component="input"
                      type="text"
                      placeholder={isMobile ? "Search jobs, credits" : "Search Feed by Jobs, Revenue Credits"}
                      value={searchQuery}
                      onChange={handleSearchChange}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        height: "100%",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontFamily: "inherit",
                        fontSize: isMobile ? "13px" : "14.5px",
                        fontWeight: 500,
                        color: "#1D2939",
                        padding: 0,
                        "&::placeholder": { color: "#9AA4B2", opacity: 1, fontWeight: 400 },
                      }}
                    />
                    {/* Clear button — only once there's something to clear. */}
                    {searchQuery && (
                      <SoftBox
                        component="button"
                        type="button"
                        onClick={() => setSearchQuery("")}
                        aria-label="Clear search"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          width: "22px",
                          height: "22px",
                          marginRight: isMobile ? "8px" : "12px",
                          border: 0,
                          borderRadius: "50%",
                          background: "rgba(15,23,42,0.08)",
                          color: "#667085",
                          cursor: "pointer",
                          transition: "background-color 120ms ease, color 120ms ease",
                          "&:hover": { background: brandRgba(brand, 0.14), color: brand },
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                      </SoftBox>
                    )}
                  </SoftBox>
                  {isAdmin && (
                  <SoftBox
                    component="button"
                    type="button"
                    onClick={handleOpenModal}
                    aria-label="Feed settings"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      width: isMobile ? "38px" : "46px",
                      height: isMobile ? "38px" : "46px",
                      cursor: "pointer",
                      background: "#EEF0F3",
                      border: "1px solid #D8DCE3",
                      borderRadius: "8px",
                      padding: 0,
                      color: "#475467",
                      transition: "background-color 150ms ease, border-color 150ms ease, color 150ms ease",
                      "&:hover": { background: brandRgba(brand, 0.1), borderColor: brand, color: brand },
                    }}
                  >
                    <TuneIcon sx={{ fontSize: isMobile ? "20px" : "22px" }} />
                  </SoftBox>
                  )}
                </SoftBox>
              </Grid>
            </Grid>
            {isMobile ? mobileSettingsPage : desktopSettingsModal}
          </Grid>
          <Grid item mini={12}>
            <FeedTabPanel value={tabValue} index={0}>
              {(!isLoading || hasMore) && !!feedPosts?.length && (
                <SoftBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { mini: "1fr", md: "minmax(0, 820px) 320px" },
                    gap: { mini: 0, md: "24px" },
                    justifyContent: "center",
                    width: "100%",
                    // Intentionally NOT setting alignItems: "start" — cells must
                    // stretch to row height so the sticky rail has room to travel.
                  }}
                >
                  <SoftBox sx={{ minWidth: 0, width: "100%" }}>
                    <InfiniteScroll
                      style={{ overflow: "visible", padding: 0 }}
                      dataLength={feedPosts.length}
                      next={() => {
                        if (isFetching || !hasMore) return;
                        setPage((prev) => Math.max(prev, currentPage) + 1);
                      }}
                      hasMore={hasMore}
                      scrollThreshold={0.85}
                      endMessage={
                        <p style={{ textAlign: "center" }}>
                          <b>Yay! You have seen it all</b>
                        </p>
                      }
                      loader={
                        <SoftBox
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 2,
                            color: "#98A2B3",
                            fontSize: "13px",
                            fontFamily: "DM Sans, sans-serif",
                          }}
                        >
                          Loading more…
                        </SoftBox>
                      }
                    >
                      <Stack sx={{ width: "100%" }}>
                        {feedPosts.map((post, index) => {
                          let key = post.job?._id;
                          if (post.postType === "yardsign") key = post.group.id;
                          else if (post.postType === "review") key = `review-${post.review._id}`;

                          let targetId = post.job?._id;
                          let targetTypeForPost = "Job";
                          if (post.postType === "yardsign") {
                            targetId = post.group.postId;
                            targetTypeForPost = "EmployeeKpi";
                          } else if (post.postType === "review") {
                            targetId = post.review._id;
                            targetTypeForPost = "Review";
                          }
                          const isScrollTarget =
                            !!scrollTarget &&
                            scrollTarget.type === targetTypeForPost &&
                            scrollTarget.id === String(targetId);

                          // First few cards mount immediately for first paint; rest lazy —
                          // except a notification's scroll target, which must exist in the
                          // DOM (with its id) as soon as its page loads, not once scrolled near.
                          const eager = index < 3 || isScrollTarget;
                          const elementId = isScrollTarget ? "feed-scroll-target" : undefined;
                          const autoOpenComments = isScrollTarget && scrollTarget.openComments;

                          let card;
                          if (post.postType === "yardsign") {
                            card = <YardSignPost group={post.group} elementId={elementId} autoOpenComments={autoOpenComments} />;
                          } else if (post.postType === "review") {
                            card = <FeedReviewCard item={post.review} elementId={elementId} autoOpenComments={autoOpenComments} />;
                          } else {
                            card = (
                              <FeedCard
                                job={post.job}
                                settings={settings}
                                rcAmount={post.job?.rcAmount}
                                rcPoints={post.job?.rcPoints}
                                elementId={elementId}
                                autoOpenComments={autoOpenComments}
                              />
                            );
                          }

                          if (eager) {
                            return <React.Fragment key={key}>{card}</React.Fragment>;
                          }

                          return <LazyFeedItem key={key}>{card}</LazyFeedItem>;
                        })}
                      </Stack>
                    </InfiniteScroll>
                  </SoftBox>
                  {!isMobile && !!kudosController.suggestions.length && (
                    <SoftBox sx={{ display: { mini: "none", md: "block" } }}>
                      <KudosRail
                        suggestions={kudosController.suggestions}
                        onClap={kudosController.onClap}
                      />
                    </SoftBox>
                  )}
                </SoftBox>
              )}
            </FeedTabPanel>

            <FeedTabPanel value={tabValue} index={1}>
              {!isLoading && !filteredJobs?.length && (
                <div className="p-[1rem] sm:p-[1.5rem] md:p-[2rem] bg-[#ffffff] feedBoxShadow rounded-2xl mb-[2rem] w-full">
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    spacing={2}
                  >
                    <Icon iconKey="broken" size={5} />
                    <Typography>No jobs found</Typography>
                  </Stack>
                </div>
              )}
            </FeedTabPanel>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
};

export default FeedPage;
